import { Section } from "@/components/Section";
import { AiAnswersCallout } from "@/components/AiAnswersCallout";
import { ServiceCoverage } from "@/components/ServiceCoverage";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CityServiceStructuredData } from "@/components/city-service/CityServiceStructuredData";
import {
  CityServiceDeliverables,
  CityServiceFaq,
  CityServiceHero,
  CityServiceOutcomes,
  CityServiceSiblings,
} from "@/components/city-service/CityServiceSections";
import type { Location } from "@/content/locations";
import { SERVICES, type Service } from "@/content/services";
import { PROGRAMMABLE_SERVICE_SLUGS, type ProgrammableServiceSlug } from "@/content/city-services";
import {
  LOCAL_FAQS,
  SERVICE_ANGLE,
  SERVICE_PILLARS,
  SERVICE_QUESTIONS,
} from "@/content/city-service-copy";
import { TOP_CITY_FAQS_EN } from "@/content/hub-faqs";

/**
 * English city × service page body. Served by /seo-services/$location/$service
 * and, for Rangpur, by /seo-services/rangpur/$area (which shadows that shape).
 */
export function CityServiceView({ loc, svc }: { loc: Location; svc: Service }) {
  const svcSlug = svc.slug as ProgrammableServiceSlug;
  const angle = SERVICE_ANGLE[svcSlug](loc);
  const pillars = SERVICE_PILLARS[svcSlug](loc);
  // City questions first, then city-market questions, then the generic service FAQs.
  const faqs = [...LOCAL_FAQS[svcSlug](loc), ...(TOP_CITY_FAQS_EN[loc.slug] ?? []), ...svc.faqs];
  const siblings = PROGRAMMABLE_SERVICE_SLUGS.filter((s) => s !== svc.slug)
    .map((s) => SERVICES.find((x) => x.slug === s)!)
    .filter(Boolean);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "SEO Locations", path: "/seo-locations" },
    { name: loc.city, path: `/seo-services/${loc.slug}` },
    { name: svc.title, path: `/seo-services/${loc.slug}/${svc.slug}` },
  ];

  return (
    <>
      <Breadcrumbs items={crumbs} />
      <CityServiceStructuredData
        loc={loc}
        svc={svc}
        angle={angle}
        pillars={pillars}
        faqs={faqs}
        crumbs={crumbs}
      />
      <CityServiceHero loc={loc} svc={svc} angle={angle} />
      <CityServiceDeliverables loc={loc} svc={svc} pillars={pillars} />
      <ServiceCoverage
        slug={loc.slug}
        city={loc.city}
        service={{ slug: svc.slug, title: svc.title }}
        areas={loc.neighborhoods}
      />
      <CityServiceOutcomes loc={loc} svc={svc} />
      <CityServiceFaq loc={loc} svc={svc} faqs={faqs} />
      <Section className="!pt-0">
        <AiAnswersCallout
          context={`${svc.title} in ${loc.city}`}
          questionSlugs={SERVICE_QUESTIONS[svcSlug]}
        />
      </Section>
      <CityServiceSiblings loc={loc} svc={svc} siblings={siblings} />
    </>
  );
}
